
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import * as XLSX from 'xlsx';

// SVG icons as components
const EditIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
  </svg>
);

const DeleteIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M14.74 9l-.346 9h-4.788l-.346-9h5.48zm.346-5a1 1 0 011 1v1H19a1 1 0 010 2H5a1 1 0 110-2h2.91V5a1 1 0 011-1h6.18z" />
  </svg>
);

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const PlusIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M12 4v16m8-8H4" />
  </svg>
);

const UserIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const EyeIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const EyeSlashIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" stroke="currentColor" strokeWidth="1.5">
    <path d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7A9.97 9.97 0 015.394 7.4M8.474 5.06A10.07 10.07 0 0112 4c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411M8.11 8.22L12 12.11m0 0l3.89 3.89M12 12.11l3.89-3.89m-3.89 3.89l-3.89 3.89" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const FilterIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width="16" 
    height="16" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9"></polyline>
  </svg>
);

// Excel Download Icon
const DownloadIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"></path>
    <polyline points="7 10 12 15 17 10"></polyline>
    <line x1="12" y1="15" x2="12" y2="3"></line>
  </svg>
);

// Toggle Switch component for active/inactive status
const ToggleSwitch = ({ isActive, onChange, isUpdating }) => {
  return (
    <div 
      onClick={isUpdating ? null : onChange} 
      style={{
        position: 'relative',
        width: '44px',
        height: '22px',
        backgroundColor: isActive ? '#52c41a' : '#f5f5f5',
        borderRadius: '11px',
        cursor: isUpdating ? 'wait' : 'pointer',
        transition: 'background-color 0.3s',
        display: 'inline-block',
        boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.1)'
      }}
    >
      <div 
        style={{
          position: 'absolute',
          top: '2px',
          left: isActive ? '24px' : '2px',
          width: '18px',
          height: '18px',
          backgroundColor: '#fff',
          borderRadius: '9px',
          transition: 'left 0.3s',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)'
        }}
      >
        {isUpdating && (
          <div 
            style={{
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              border: '2px solid #f3f3f3',
              borderTop: '2px solid #3498db',
              animation: 'spin 1s linear infinite'
            }}
          />
        )}
      </div>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

const BlogManagement = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchText, setSearchText] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // New state for filtering by status
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false); // New state for status dropdown
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [currentBlog, setCurrentBlog] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null); // Track which blog's status is being updated
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    category: "",
    authorName: "",
    metaTitle: "",
    metaDescription: "",
    slug: "",
    keywords: "",
    isActive: true, // Added isActive field to form data
  });
  const [exportLoading, setExportLoading] = useState(false); // Track export loading state

  const categoryColors = {
    "Cyber Security": "#52c41a",
    "HR & Job": "#fa8c16",
    "IT Technology": "#13c2c2",
    "BFSI": "#722ed1",
    "Education": "#eb2f96",
    "Contact Center": "#faad14",
    "Sales Marketing": "#faad14",
    "Digital Marketing": "#faad14",
    "Others": "#a0a0a0",
  };

  // Available categories for dropdown
  const categories = [
    "HR & Job",
    "IT Technology",
    "BFSI",
    "Education",
    "Cyber Security",
    "Contact Center",
    "Sales Marketing",
    "Digital Marketing",
    "Others",
  ];

  // Status options for filtering
  const statusOptions = [
    { value: "all", label: "All Posts" },
    { value: "active", label: "Published Only" },
    { value: "inactive", label: "Draft Only" },
  ];

  useEffect(() => {
    fetchBlogs();
  }, [pagination.current, pagination.pageSize, searchText, categoryFilter, statusFilter]);

  const fetchBlogs = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        search: searchText,
        category: categoryFilter,
      };

      // Add status filter if not showing all
      if (statusFilter !== "all") {
        params.isActive = statusFilter === "active";
      }

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/postblog/`,
        { params }
      );
    
      setBlogs(response.data.data || []);
      setPagination({
        ...pagination,
        total: response.data.totalPosts || 0,
      });
    } catch (error) {
      console.error("Failed to fetch blogs", error);
      alert("Failed to fetch blogs");
    } finally {
      setLoading(false);
    }
  };

  // Export to Excel functionality
  const exportToExcel = async () => {
    setExportLoading(true);
    try {
      // Get all blogs (not just current page)
      const params = {
        // Remove pagination to get all blogs
        search: searchText,
        category: categoryFilter,
      };

      // Add status filter if not showing all
      if (statusFilter !== "all") {
        params.isActive = statusFilter === "active";
      }

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/postblog/`,
        { params }
      );
      
      const allBlogs = response.data.data || [];
      
      // Format data for Excel
      const worksheetData = allBlogs.map(blog => ({
        'Title': blog.title,
        'Author': blog.authorName,
        'Category': blog.category,
        'Date': new Date(blog.createdAt).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        }),
        'Status': blog.isActive ? 'Published' : 'Draft',
        'Meta Title': blog.metaTitle || '',
        'Meta Description': blog.metaDescription || '',
        'Slug': blog.slug || '',
        'Keywords': blog.keywords || '',
        'ID': blog._id,
        'Created At': new Date(blog.createdAt).toLocaleString(),
        'Updated At': blog.updatedAt ? new Date(blog.updatedAt).toLocaleString() : ''
      }));
      
      // Create worksheet
      const worksheet = XLSX.utils.json_to_sheet(worksheetData);
      
      // Create workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Blogs");
      
      // Auto-size columns
      const maxWidths = {};
      
      // Get maximum width for each column including header
      if (worksheetData.length > 0) {
        Object.keys(worksheetData[0]).forEach((key, i) => {
          const columnIndex = i;
          maxWidths[columnIndex] = key.length;
          
          worksheetData.forEach(row => {
            const cellValue = row[key] || '';
            const cellWidth = String(cellValue).length;
            maxWidths[columnIndex] = Math.max(maxWidths[columnIndex], cellWidth);
          });
        });
        
        // Set column widths
        worksheet['!cols'] = Object.keys(maxWidths).map(key => ({
          wch: maxWidths[key] + 2 // Adding padding
        }));
      }
      
      // Generate Excel file
      const today = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `blog_export_${today}.xlsx`);
      
      alert("Blogs exported successfully!");
    } catch (error) {
      console.error("Failed to export blogs", error);
      alert("Failed to export blogs to Excel");
    } finally {
      setExportLoading(false);
    }
  };

  const getCategoryColor = (category) => {
    return categoryColors[category] || "#108ee9";
  };

  const handleEdit = (blog) => {
    setCurrentBlog(blog);
    setFormData({
      title: blog.title,
      content: blog.content || "",
      category: blog.category || "",
      authorName: blog.authorName || "",
      metaTitle: blog.metaTitle || "",
      metaDescription: blog.metaDescription || "",
      slug: blog.slug || "",
      keywords: blog.keywords || "",
      image: blog.image || "",
      isActive: blog.isActive !== undefined ? blog.isActive : true, // Set isActive from blog data
    });
    setEditModalVisible(true);
  };

  const handleUpdateBlog = async () => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/postblog/${currentBlog._id}`,
        formData
      );
      alert("Blog updated successfully");
      setEditModalVisible(false);
      fetchBlogs();
    } catch (error) {
      console.error("Failed to update blog", error);
      alert("Failed to update blog");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/postblog/${id}`
      );
      alert("Blog deleted successfully");
      fetchBlogs();
    } catch (error) {
      console.error("Failed to delete blog", error);
      alert("Failed to delete blog");
    }
  };

  // Handler for toggling blog active status - FIXED VERSION
  const handleToggleStatus = async (blogId, currentStatus) => {
    setUpdatingStatus(blogId);
    try {
      // debugging
      console.log('Toggle request for blog ID:', blogId);
      console.log('Current status:', currentStatus);
      console.log('API URL:', `${process.env.REACT_APP_BACKEND_BASEURL}/api/postblog/${blogId}/status`);
      

      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/postblog/${blogId}`,
        { isActive: !currentStatus }
      );
      
      console.log('Response:', response);
      
      
      setBlogs(blogs.map(blog => 
        blog._id === blogId ? { ...blog, isActive: !currentStatus } : blog
      ));
      
      
      console.log('Status updated successfully');
    } catch (error) {
      console.error("Failed to update blog status:", error);
      console.error("Error details:", error.response ? error.response.data : "No response data");
      alert("Failed to update blog status");
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleCategorySelect = (category) => {
    setCategoryFilter(category);
    setShowCategoryDropdown(false);
  };

  const handleStatusSelect = (status) => {
    setStatusFilter(status);
    setShowStatusDropdown(false);
  };

  const handleAddNewPost = () => {
    navigate("/postBlog");
  };

  
  return (
    <div
      style={{ padding: "30px", background: "#f0f2f5", minHeight: "100vh" }}
    >
      <div
        style={{
          backgroundColor: "white",
          boxShadow: "0 6px 16px rgba(0, 0, 0, 0.08)",
          borderRadius: "12px",
          padding: "20px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "30px",
            padding: "0 0 16px 0",
            borderBottom: "1px solid #f0f0f0",
          }}
        >
          <div>
            <h3 style={{ margin: 0, color: "#1890ff" }}>
              Blog Posts Management
            </h3>
            <p style={{ color: "#666" }}>Manage all your blog posts</p>
          </div>
          <div style={{ display: "flex", gap: "10px" }}>
            <button
              style={{
                backgroundColor: "#52c41a",
                color: "white",
                border: "none",
                borderRadius: "8px",
                padding: "10px 15px",
                cursor: exportLoading ? "wait" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                opacity: exportLoading ? 0.7 : 1,
              }}
              onClick={exportToExcel}
              disabled={exportLoading}
            >
              {exportLoading ? (
                <span>Exporting...</span>
              ) : (
                <>
                  <DownloadIcon />
                  Export to Excel
                </>
              )}
            </button>
            <button
              style={{
                backgroundColor: "#1890ff",
                color: "white",
                border: "none",
                borderRadius: "8px",
                padding: "10px 15px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
              onClick={handleAddNewPost}
            >
              <span style={{ display: "flex", alignItems: "center" }}>
                <PlusIcon />
              </span>
              Add New Post
            </button>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            marginBottom: "24px",
            background: "#f9fafc",
            padding: "20px",
            borderRadius: "10px",
            border: "1px solid #f0f0f0",
          }}
        >
          <div style={{ position: "relative", width: "100%" }}>
            <input
              type="text"
              placeholder="Search blogs by title or author"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{
                width: "100%",
                padding: "10px 10px 10px 36px",
                borderRadius: "8px",
                border: "1px solid #d9d9d9",
              }}
            />
            <span
              style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#bfbfbf",
              }}
            >
              <SearchIcon />
            </span>
          </div>
          <button
            style={{
              backgroundColor: "#1890ff",
              color: "white",
              border: "none",
              borderRadius: "8px",
              padding: "10px 15px",
              marginLeft: "10px",
              cursor: "pointer",
            }}
            onClick={() => fetchBlogs()}
          >
            Search
          </button>
        </div>

        {/* Filter Controls */}
        <div style={{ 
          display: "flex", 
          gap: "15px", 
          marginBottom: "20px",
          alignItems: "center" 
        }}>
          {/* Category Filter */}
          <div style={{ position: "relative" }}>
            <button
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 12px",
                borderRadius: "8px",
                border: "1px solid #d9d9d9",
                backgroundColor: "white",
                cursor: "pointer",
              }}
              onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
            >
              <span style={{ display: "flex", alignItems: "center" }}>
                <FilterIcon />
              </span>
              Category: {categoryFilter || "All"}
              <ChevronDownIcon />
            </button>
            
            {showCategoryDropdown && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  backgroundColor: "white",
                  border: "1px solid #d9d9d9",
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                  padding: "8px 0",
                  zIndex: 10,
                  width: "180px",
                }}
              >
                <div
                  style={{
                    padding: "8px 12px",
                    cursor: "pointer",
                    backgroundColor: categoryFilter === "" ? "#e6f7ff" : "transparent",
                    fontWeight: categoryFilter === "" ? "bold" : "normal",
                  }}
                  onClick={() => handleCategorySelect("")}
                >
                  All Categories
                </div>
                {categories.map((category) => (
                  <div
                    key={category}
                    style={{
                      padding: "8px 12px",
                      cursor: "pointer",
                      backgroundColor: categoryFilter === category ? "#e6f7ff" : "transparent",
                      fontWeight: categoryFilter === category ? "bold" : "normal",
                      display: "flex",
                      alignItems: "center",
                    }}
                    onClick={() => handleCategorySelect(category)}
                  >
                    <span
                      style={{
                        display: "inline-block",
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        backgroundColor: getCategoryColor(category),
                        marginRight: "8px",
                      }}
                    ></span>
                    {category}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Status Filter */}
          <div style={{ position: "relative" }}>
            <button
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 12px",
                borderRadius: "8px",
                border: "1px solid #d9d9d9",
                backgroundColor: "white",
                cursor: "pointer",
              }}
              onClick={() => setShowStatusDropdown(!showStatusDropdown)}
            >
              <span style={{ display: "flex", alignItems: "center" }}>
                {statusFilter === "active" ? <EyeIcon /> : 
                 statusFilter === "inactive" ? <EyeSlashIcon /> : <EyeIcon />}
              </span>
              Status: {statusOptions.find(s => s.value === statusFilter)?.label || "All"}
              <ChevronDownIcon />
            </button>
            
            {showStatusDropdown && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  backgroundColor: "white",
                  border: "1px solid #d9d9d9",
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                  padding: "8px 0",
                  zIndex: 10,
                  width: "180px",
                }}
              >
                {statusOptions.map((status) => (
                  <div
                    key={status.value}
                    style={{
                      padding: "8px 12px",
                      cursor: "pointer",
                      backgroundColor: statusFilter === status.value ? "#e6f7ff" : "transparent",
                      fontWeight: statusFilter === status.value ? "bold" : "normal",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px"
                    }}
                    onClick={() => handleStatusSelect(status.value)}
                  >
                    {status.value === "active" ? <EyeIcon /> : 
                     status.value === "inactive" ? <EyeSlashIcon /> : <EyeIcon />}
                    {status.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Blog List Table - Updated with Status Column */}
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr
                style={{
                  borderBottom: "2px solid #f0f0f0",
                  color: "#fff",
                  fontWeight: "bold",
                  background: "linear-gradient(to right, #1890ff, #096dd9)",
                }}
              >
                <th style={{ padding: "16px", textAlign: "left" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    Title
                    <span style={{ cursor: "pointer" }}>↓</span>
                  </div>
                </th>
                <th style={{ padding: "16px", textAlign: "left" }}>Author</th>
                <th style={{ padding: "16px", textAlign: "left" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      position: "relative",
                    }}
                  >
                    Categories
                  </div>
                </th>
                <th style={{ padding: "16px", textAlign: "left" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    Date
                    <span style={{ cursor: "pointer" }}>↓</span>
                  </div>
                </th>
                <th style={{ padding: "16px", textAlign: "center" }}>Status</th>
                <th style={{ padding: "16px", textAlign: "center" }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan="6"
                    style={{ textAlign: "center", padding: "20px" }}
                  >
                    Loading...
                  </td>
                </tr>
              ) : blogs.length === 0 ? (
                <tr>
                  <td
                    colSpan="6"
                    style={{ textAlign: "center", padding: "20px" }}
                  >
                    No blogs found
                  </td>
                </tr>
              ) : (
                blogs.map((blog, index) => (
                  <tr
                    key={blog._id}
                    style={{
                      borderBottom: "1px solid #f0f0f0",
                      backgroundColor:
                        index % 2 === 0 ? "#ffffff" : "#f9fafc",
                    }}
                  >
                    <td style={{ padding: "16px" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        <div
                          style={{
                            width: "4px",
                            height: "40px",
                            backgroundColor: getCategoryColor(blog.category),
                            borderRadius: "4px",
                          }}
                        ></div>
                       
                        <div style={{ fontWeight: "bold" }}>{blog.title}</div>
                      </div>
                    </td>
                    <td style={{ padding: "16px" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <div
                          style={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "50%",
                            backgroundColor: "#1890ff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                          }}
                        >
                          <UserIcon />
                        </div>
                        {blog.authorName}
                      </div>
                    </td>
                    <td style={{ padding: "16px" }}>
                      <span
                        style={{
                          backgroundColor: getCategoryColor(blog.category),
                          color: "white",
                          padding: "4px 8px",
                          borderRadius: "12px",
                          fontSize: "14px",
                        }}
                      >
                        {blog.category}
                      </span>
                    </td>
                  <td style={{ padding: "16px" }}>
                    <div style={{
                             display: "flex",
                             alignItems: "center",
                             gap: "5px",
                             backgroundColor: "rgba(0, 201, 167, 0.1)",
                             padding: "4px 8px",
                             borderRadius: "12px",
                             color: "#00c9a7",
                             fontSize: "14px",
                             width: "fit-content",
                           }} >
                  <CalendarIcon />
                   {new Date(blog.createdAt).toLocaleDateString(
                          "en-US",
                        {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        }
                        )}{" "}
                     {new Date(blog.createdAt).toLocaleTimeString(
                      "en-US",
                    {
                        hour: "2-digit",
                        minute: "2-digit",
                    }
                    )}
                       </div>
                       </td>
                       
                    <td style={{ padding: "16px", textAlign: "center" }}>
                      <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                        <ToggleSwitch 
                          isActive={blog.isActive !== undefined ? blog.isActive : true}
                          onChange={() => handleToggleStatus(blog._id, blog.isActive)} 
                          isUpdating={updatingStatus === blog._id}
                        />
                        <span style={{ 
                          marginLeft: "8px", 
                          fontSize: "14px",
                          color: blog.isActive ? "#52c41a" : "#bfbfbf"
                        }}>
                          {blog.isActive ? "Published" : "Draft"}
                        </span>
                      </div>
                    </td>
                    <td style={{ padding: "16px", textAlign: "center" }}>
                      <div
                        style={{
                          display: "flex",
                          gap: "10px",
                          justifyContent: "center",
                        }}
                      >
                        <button
                          style={{
                            backgroundColor: "#e6f7ff",
                            color: "#1890ff",
                            border: "none",
                            borderRadius: "6px",
                            padding: "8px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "32px",
                            height: "32px",
                          }}
                          onClick={() => handleEdit(blog)}
                          title="Edit"
                        >
                          <EditIcon />
                        </button>
                      
                        <button
                          style={{
                            backgroundColor: "#fff1f0",
                            color: "#ff4d4f",
                            border: "none",
                            borderRadius: "6px",
                            padding: "8px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "32px",
                            height: "32px",
                          }}
                          onClick={() => {
                            if (
                              window.confirm(
                                "Are you sure you want to delete this blog?"
                              )
                            ) {
                              handleDelete(blog._id);
                            }
                          }}
                          title="Delete"
                        >
                          <DeleteIcon />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Simple Pagination */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "20px",
            gap: "10px",
          }}
        >
          <button
            style={{
              padding: "5px 10px",
              borderRadius: "4px",
              border: "1px solid #d9d9d9",
              backgroundColor: "white",
              cursor: pagination.current > 1 ? "pointer" : "not-allowed",
              opacity: pagination.current > 1 ? 1 : 0.5,
            }}
            onClick={() => {
              if (pagination.current > 1) {
                setPagination({
                  ...pagination,
                  current: pagination.current - 1,
                });
              }
            }}
            disabled={pagination.current <= 1}
          >
            Prev..
          </button>
          <span style={{ padding: "5px 10px" }}>
            Page {pagination.current} of{" "}
            {Math.ceil(pagination.total / pagination.pageSize) || 1}
          </span>
          <button
            style={{
              padding: "5px 10px",
              borderRadius: "4px",
              border: "3px solid #d9d9d9",
              background: "linear-gradient(to right, #1890ff, #096dd9)",
              color: "white",
              cursor:
                pagination.current <
                Math.ceil(pagination.total / pagination.pageSize)
                  ? "pointer"
                  : "not-allowed",
              opacity:
                pagination.current <
                Math.ceil(pagination.total / pagination.pageSize)
                  ? 1
                  : 0.5,
            }}
            onClick={() => {
              if (
                pagination.current <
                Math.ceil(pagination.total / pagination.pageSize)
              ) {
                setPagination({
                  ...pagination,
                  current: pagination.current + 1,
                });
              }
            }}
            disabled={
              pagination.current >=
              Math.ceil(pagination.total / pagination.pageSize)
            }
          >
            Next
          </button>
        </div>
      </div>

      {/* Edit Modal - Enhanced with isActive toggle */}
      {editModalVisible && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "24px",
              borderRadius: "12px",
              width: "800px",
              maxWidth: "90%",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            <h3 style={{ marginTop: 0, color: "#1890ff" }}>Edit Blog Post</h3>
      
            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "bold",
                }}
              >
                <span style={{ color: "red" }}>*</span> Title
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                style={{
                  width: "100%",
                  padding: "10px",
                  borderRadius: "8px",
                  border: "1px solid #d9d9d9",
                }}
              />
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "bold",
                }}
              >
                Meta Title (for SEO)
              </label>
              <input
                type="text"
                name="metaTitle"
                value={formData.metaTitle}
                onChange={handleInputChange}
                style={{
                  width: "100%",
                  padding: "10px",
                  borderRadius: "8px",
                  border: "1px solid #d9d9d9",
                }}
              />
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "bold",
                }}
              >
                Meta Description (for SEO)
              </label>
              <textarea
                name="metaDescription"
                value={formData.metaDescription}
                onChange={handleInputChange}
                rows="3"
                style={{
                  width: "100%",
                  padding: "10px",
                  borderRadius: "8px",
                  border: "1px solid #d9d9d9",
                  resize: "vertical",
                }}
              />
            </div>

            {/* Publication Status Toggle in Edit Modal */}
            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "bold",
                }}
              >
                Publication Status
              </label>
              <div style={{ 
                display: "flex", 
                alignItems: "center",
                gap: "10px"
              }}>
                <ToggleSwitch 
                  isActive={formData.isActive} 
                  onChange={() => setFormData({...formData, isActive: !formData.isActive})} 
                  isUpdating={false}
                />
                <span style={{ color: formData.isActive ? "#52c41a" : "#bfbfbf" }}>
                  {formData.isActive ? "Published (visible to all visitors)" : "Draft (only visible to admins)"}
                </span>
              </div>
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "bold",
                }}
              >
                <span style={{ color: "red" }}>*</span> Category
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                style={{
                  width: "100%",
                  padding: "10px",
                  borderRadius: "8px",
                  border: "1px solid #d9d9d9",
                }}
              >
                <option value="">Select a category</option>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "bold",
                }}
              >
                Blog URL (friendly version of title)
              </label>
              <input
                type="text"
                name="slug"
                value={formData.slug}
                onChange={handleInputChange}
                style={{
                  width: "100%",
                  padding: "10px",
                  borderRadius: "8px",
                  border: "1px solid #d9d9d9",
                }}
              />
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "bold",
                }}
              >
                Keywords (comma separated)
              </label>
              <input
                type="text"
                name="keywords"
                value={formData.keywords}
                onChange={handleInputChange}
                style={{
                  width: "100%",
                  padding: "10px",
                  borderRadius: "8px",
                  border: "1px solid #d9d9d9",
                }}
              />
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "bold",
                }}
              >
                <span style={{ color: "red" }}>*</span> Author Name
              </label>
              <input
                type="text"
                name="authorName"
                value={formData.authorName}
                onChange={handleInputChange}
                style={{
                  width: "100%",
                  padding: "10px",
                  borderRadius: "8px",
                  border: "1px solid #d9d9d9",
                }}
              />
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "bold",
              }}>
                <span style={{ color: "red" }}>*</span> Content
              </label>
              
              <ReactQuill
                theme="snow"
                value={formData.content}
                onChange={(value) => setFormData({...formData, content: value})}
                modules={{
                  toolbar: [
                    [{ 'header': [1, 2, 3, 4, false] }],
                    ['bold', 'italic', 'underline', 'strike'],
                    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                    ['link', 'image'],
                    ['clean']
                  ],
                }}
                style={{
                  height: '250px',
                  marginBottom: '50px',
                  borderRadius: "8px",
                }}
              />
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "bold",
                }}
              >
                Featured Image
              </label>
              {formData.image && (
                <img 
                  src={formData.image} 
                  alt="Featured" 
                  style={{
                    maxWidth: "100%", 
                    height: "auto", 
                    marginBottom: "10px",
                    borderRadius: "8px",
                    maxHeight: "200px"
                  }}
                />
              )}
              <input 
                type="file"
                accept="image/*"
                style={{ 
                  width: "100%", 
                  marginTop: "10px" 
                }}
              />
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
                marginTop: "24px",
              }}
            >
              <button
                style={{
                  padding: "10px 15px",
                  borderRadius: "8px",
                  border: "1px solid #d9d9d9",
                  backgroundColor: "white",
                  cursor: "pointer",
                }}
                onClick={() => setEditModalVisible(false)}
              >
                Cancel
              </button>
              <button
                style={{
                  padding: "10px 15px",
                  borderRadius: "8px",
                  border: "none",
                  backgroundColor: "#1890ff",
                  color: "white",
                  cursor: "pointer",
                }}
                onClick={handleUpdateBlog}
              >
                Update Post
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogManagement;